import { useEffect, useState, useMemo, useRef } from "react";
import { useSelector } from "react-redux";
import { useParams, useNavigate } from "react-router-dom";
import DetailsTab from "./tabs/DetailsTab";
import RulesTab from "./tabs/RulesTab";
import TeamsTab from "./tabs/Teams";
import StatsTab from "./tabs/Stats";
import LoadingSpinner from "../../utils/general/LoadingSpinner";
import StandingsTab from "./tabs/StandingsTab";
import TournamentTabs from "./tabs/TournamentTabs";
import { ArrowLeft, ChevronLeft, ChevronRight } from "lucide-react";

const TournamentDetailsPage = () => {
  const token = useSelector((state) => state.user.token);
  const { id } = useParams();
  const navigate = useNavigate();

  const [tournament, setTournament] = useState(null);
  const [error, setError] = useState("");
  const [activeTab, setActiveTab] = useState("posiciones");
  const [selectedSubcategory, setSelectedSubcategory] = useState(null);
  const [showLeftArrow, setShowLeftArrow] = useState(false);
  const [showRightArrow, setShowRightArrow] = useState(true);
  const tabsContainerRef = useRef(null);

  useEffect(() => {
    const fetchTournamentDetails = async () => {
      try {
        const response = await fetch(
          `${process.env.REACT_APP_API_URL}/api/tournaments/${id}`,
          {
            method: "GET",
            headers: {
              Authorization: `Bearer ${token}`,
            },
          },
        );

        if (!response.ok) {
          throw new Error("Error al obtener detalles del torneo.");
        }

        const data = await response.json();
        setTournament(data);
        if (data.subcategories && data.subcategories.length > 0) {
          setSelectedSubcategory(data.subcategories[0]);
        }
      } catch (error) {
        setError("Error al obtener detalles del torneo.");
        console.error("Error:", error);
      }
    };

    fetchTournamentDetails();
  }, [id]);

  // Efecto para comprobar la visibilidad de las flechas de scroll
  useEffect(() => {
    const checkScroll = () => {
      if (tabsContainerRef.current) {
        const { scrollLeft, scrollWidth, clientWidth } =
          tabsContainerRef.current;
        setShowLeftArrow(scrollLeft > 0);
        setShowRightArrow(scrollLeft < scrollWidth - clientWidth - 10);
      }
    };

    // Comprobar al montar y al redimensionar
    checkScroll();
    window.addEventListener("resize", checkScroll);

    // Comprobar periódicamente por si el contenido cambia
    const interval = setInterval(checkScroll, 500);

    return () => {
      window.removeEventListener("resize", checkScroll);
      clearInterval(interval);
    };
  }, [tournament, activeTab]);

  // Función para hacer scroll horizontal
  const scrollTabs = (direction) => {
    if (tabsContainerRef.current) {
      const scrollAmount = 200;
      tabsContainerRef.current.scrollBy({
        left: direction === "right" ? scrollAmount : -scrollAmount,
        behavior: "smooth",
      });
    }
  };

  // Función para ordenar subcategorías (ignorando number = 0, null, undefined)
  const sortSubcategories = (subcategories) => {
    return [...subcategories].sort((a, b) => {
      const aHasNumber =
        a.number !== null && a.number !== undefined && a.number !== 0;
      const bHasNumber =
        b.number !== null && b.number !== undefined && b.number !== 0;

      if (aHasNumber && bHasNumber) {
        if (a.number !== b.number) {
          return a.number - b.number; // Orden numérico ascendente
        }
        return (a.group_name || "").localeCompare(b.group_name || "");
      }

      if (aHasNumber && !bHasNumber) return -1;
      if (!aHasNumber && bHasNumber) return 1;

      return (a.group_name || "").localeCompare(b.group_name || "");
    });
  };

  const sortedSubcategories = useMemo(() => {
    if (!tournament || !tournament.subcategories) return [];
    return sortSubcategories(tournament.subcategories);
  }, [tournament]);

  // Función para mostrar el display de forma consistente
  const formatSubcategoryDisplay = (subcategory) => {
    if (!subcategory.number || subcategory.number === 0) {
      return subcategory.group_name || "Sin categoría";
    }
    if (subcategory.group_name) {
      return `${subcategory.number} ${subcategory.group_name}`;
    }
    return subcategory.number.toString();
  };

  if (error) return <p className="text-red-500">{error}</p>;
  if (!tournament) return <LoadingSpinner />;

  return (
    <div className="bg-gray-50 min-h-screen font-sans p-4">
      <div className="max-w-2xl mx-auto p-4">
        <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700;800&display=swap');
        body { font-family: 'Inter', sans-serif; }
      `}</style>

        {/* Header del torneo */}
        <div className="flex items-center gap-4 p-4 rounded-3xl bg-gradient-to-r from-emerald-500 to-emerald-600 text-white shadow-md mb-6">
          <button
            onClick={() => navigate(-1)}
            className="text-white hover:bg-white/20 p-2 rounded-full transition"
          >
            <ArrowLeft size={22} strokeWidth={2.5} />
          </button>
          <img
            src={
              tournament.picture_url ||
              "https://images.icon-icons.com/1193/PNG/512/1490877818-sport-badges05_82419.png"
            }
            alt={tournament.tournament_name}
            className="w-16 h-16 rounded-xl object-cover"
            onError={(e) => {
              e.target.onerror = null;
              e.target.src =
                "https://images.icon-icons.com/1193/PNG/512/1490877818-sport-badges05_82419.png";
            }}
          />
          <div>
            <h1 className="text-xl font-bold">{tournament.tournament_name}</h1>
            <p className="text-sm opacity-90">Torneo</p>
          </div>
        </div>

        {/* Subcategorías */}
        {sortedSubcategories.length > 0 && (
          <div className="overflow-x-auto mb-4">
            <div className="flex gap-3">
              {sortedSubcategories.map((subcategory) => (
                <button
                  key={subcategory.id}
                  className={`px-4 py-2 rounded-2xl whitespace-nowrap font-medium transition-colors ${
                    selectedSubcategory?.id === subcategory.id
                      ? "bg-emerald-500 text-white shadow-md"
                      : "bg-gray-200 text-gray-700 hover:bg-gray-300"
                  }`}
                  onClick={() => setSelectedSubcategory(subcategory)}
                >
                  {formatSubcategoryDisplay(subcategory)}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Tabs con scroll horizontal */}
        <div className="relative mb-4">
          {/* Flecha izquierda */}
          {showLeftArrow && (
            <button
              onClick={() => scrollTabs("left")}
              className="absolute left-0 top-1/2 transform -translate-y-1/2 z-10 bg-white rounded-full p-1 shadow-md hover:bg-gray-100 transition"
              style={{ top: "50%" }}
            >
              <ChevronLeft size={20} className="text-gray-600" />
            </button>
          )}

          {/* Flecha derecha */}
          {showRightArrow && (
            <button
              onClick={() => scrollTabs("right")}
              className="absolute right-0 top-1/2 transform -translate-y-1/2 z-10 bg-white rounded-full p-1 shadow-md hover:bg-gray-100 transition"
              style={{ top: "50%" }}
            >
              <ChevronRight size={20} className="text-gray-600" />
            </button>
          )}

          {/* Contenedor de tabs con scroll */}
          <div
            ref={tabsContainerRef}
            className="flex overflow-x-auto scrollbar-hide bg-white rounded-2xl shadow-md p-2 scroll-smooth"
            style={{ scrollbarWidth: "none", msOverflowStyle: "none" }}
          >
            {[
              "posiciones",
              "equipos",
              "partidos",
              "estadísticas",
              "detalles",
              "reglas",
            ].map((tab) => (
              <button
                key={tab}
                onClick={() => setActiveTab(tab)}
                className={`flex-shrink-0 px-4 py-2 rounded-xl font-semibold transition-colors whitespace-nowrap ${
                  activeTab === tab
                    ? "bg-emerald-500 text-white shadow"
                    : "text-gray-500 hover:bg-gray-100"
                }`}
              >
                {tab.charAt(0).toUpperCase() + tab.slice(1)}
              </button>
            ))}
          </div>
        </div>

        {/* Contenido del tab */}
        <div className="bg-white rounded-3xl shadow p-4">
          {activeTab === "detalles" && <DetailsTab tournament={tournament} />}
          {activeTab === "reglas" && <RulesTab tournament={tournament} />}
          {activeTab === "partidos" && (
            <TournamentTabs
              tournament={tournament}
              selectedSubcategory={selectedSubcategory}
            />
          )}
          {activeTab === "equipos" && (
            <TeamsTab
              key={selectedSubcategory?.id || "no-sub"}
              tournament={tournament}
              selectedSubcategory={selectedSubcategory}
            />
          )}
          {activeTab === "estadísticas" && (
            <StatsTab
              tournament={tournament}
              selectedSubcategory={selectedSubcategory}
            />
          )}
          {activeTab === "posiciones" && (
            <StandingsTab
              key={selectedSubcategory?.id || "no-sub"}
              tournament={tournament}
              selectedSubcategory={selectedSubcategory}
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default TournamentDetailsPage;
